// @ts-expect-error
import { generateRoomWithoutSeparator } from '@jitsi/js-utils/random';
import VisioGateApp from '../visio-gate/VisioGateApp';
import { getTokenAuthUrl } from '../authentication/functions.web';
import { isRoomValid } from '../base/conference/functions';
import { isSupportedBrowser } from '../base/environment/environment';
import { browser } from '../base/lib-jitsi-meet';
import { toState } from '../base/redux/functions';
import { parseURIString } from '../base/util/uri';
import Conference from '../conference/components/web/Conference';
import { getDeepLinkingPage } from '../deep-linking/functions';
import UnsupportedDesktopBrowser from '../unsupported-browser/components/UnsupportedDesktopBrowser';
import BlankPage from '../welcome/components/BlankPage.web';
import WelcomePage from '../welcome/components/WelcomePage.web';
import { getCustomLandingPageURL, isWelcomePageEnabled } from '../welcome/functions';
/**
 * Determines which route is to be rendered in order to depict a specific Redux
 * store.
 *
 * @param {(Function|Object)} stateful - THe redux store, state, or
 * {@code getState} function.
 * @returns {Promise<Object>}
 */
export function _getRouteToRender(stateful) {
    const state = toState(stateful);
    // ✅ 0) VisioGate a priorité (avant conference/welcome)
    const visioGateRoute = _getWebVisioGateRoute(state);
    if (visioGateRoute) {
        return Promise.resolve(visioGateRoute);
    }
    return _getWebConferenceRoute(state) || _getWebWelcomePageRoute(state);
}
/**
 * Route VisioGate : si on arrive avec sessionId/courseSessionId (ex: iframe Vue)
 * et qu'on n'a pas déjà validé l'auth.
 */
function _getWebVisioGateRoute(_state) {
    try {
        const url = new URL(window.location.href);
        const p = url.searchParams;
        const alreadyOk = false; // 🔧 DEBUG: Toujours afficher VisioGate pour les tests
        // on déclenche le gate uniquement si on est sur une page "gate"
        // ex: /visio-gate OU /FormationXXX (comme tu construis côté Vue)
        // OU si on a un sessionId/courseSessionId en paramètre (priorité absolue)
        const isGatePath = url.pathname.startsWith('/visio-gate') ||
            url.pathname.startsWith('/Formation') ||
            url.pathname.includes('Formation');
        const hasSessionId = p.has('sessionId') || p.has('courseSessionId');
        // IMPORTANT: on ne force jamais le gate si on est déjà sur une room (ex: /cs_xxx)
        const looksLikeRoom = /^\/cs_[^/]+/.test(url.pathname);
        if (!looksLikeRoom && (isGatePath || hasSessionId) && !alreadyOk) {
            const route = _getEmptyRoute();
            route.component = VisioGateApp;
            route.href = undefined;
            return route;
        }
    }
    catch (e) {
    }
    return;
}
/**
 * Returns the Route to display when trying to access a conference if
 * a valid conference is being joined.
 *
 * @param {Object} state - The redux state.
 * @returns {Promise|undefined}
 */
function _getWebConferenceRoute(state) {
    const room = state['features/base/conference'].room;
    if (!isRoomValid(room)) {
        return;
    }
    const route = _getEmptyRoute();
    const config = state['features/base/config'];
    // if we have auto redirect enabled, and we have previously logged in successfully
    // let's redirect to the auth url to get the token and login again if
    if (!browser.isElectron()
        && config.tokenAuthUrl
        && config.tokenAuthUrlAutoRedirect
        && state['features/authentication'].tokenAuthUrlSuccessful
        && !state['features/base/jwt'].jwt
        && room) {
        const url = new URL(window.location.href);
        const { tenant } = parseURIString(url.href) || {};
        const { startAudioOnly } = config;
        return getTokenAuthUrl(config, url, {
            audioMuted: false,
            audioOnlyEnabled: startAudioOnly,
            skipPrejoin: false,
            videoMuted: false
        }, room, tenant)
            .then((url) => {
            route.href = url;
            return route;
        })
            .catch(() => Promise.resolve(route));
    }
    // Update the location if it doesn't match. This happens when a room is
    // joined from the welcome page. The reason for doing this instead of using
    // the history API is that we want to load the config.js which takes the
    // room into account.
    const currentUrl = window.location.href;
    return getDeepLinkingPage(state)
        .then(deepLinkComponent => {
        if (deepLinkComponent) {
            route.component = deepLinkComponent;
        }
        else if (isSupportedBrowser()) {
            route.component = Conference;
        }
        else {
            route.component = UnsupportedDesktopBrowser;
        }
        return route;
    });
}
/**
 * Returns the {@code Route} to display when trying to access the welcome page.
 *
 * @param {Object} state - The redux state.
 * @returns {Promise<Object>}
 */
function _getWebWelcomePageRoute(state) {
    const route = _getEmptyRoute();
    if (isWelcomePageEnabled(state)) {
        if (isSupportedBrowser()) {
            const customLandingPage = getCustomLandingPageURL(state);
            if (customLandingPage) {
                route.href = customLandingPage;
            }
            else {
                route.component = WelcomePage;
            }
        }
        else {
            route.component = UnsupportedDesktopBrowser;
        }
    }
    else {
        // Web: if the welcome page is disabled, go directly to a random room.
        const url = new URL(window.location.href);
        url.pathname += generateRoomWithoutSeparator();
        route.href = url.href;
    }
    return Promise.resolve(route);
}
/**
 * Returns the default {@code Route}.
 *
 * @returns {Object}
 */
function _getEmptyRoute() {
    return {
        component: BlankPage,
        href: undefined
    };
}
